import useDiscoveryFetch from './useDiscoveryFetch';
import './cityDetail/CityDiscoveryListMobile.css';
import MobileDiscoveryHeading from './MobileDiscoveryHeading';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Table, Form, Row, Col, Card } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Pagination from 'react-bootstrap/Pagination';
import { useMediaQuery } from 'react-responsive';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import api from '../../api/axiosInstance';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';
import defaultLogo from '../../assets/img/ic-img.svg';
import defaultCompanyMarkerLogo from '../../assets/img/ic-building-company.svg';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import DiscoveryMap from './DiscoveryMap';
import DiscoveryFilterBar from './DiscoveryFilterBar';
import CityDetailHero from './cityDetail/CityDetailHero';
import CityDetailMobileTabs from './cityDetail/CityDetailMobileTabs';
import normalizeUrl from './cityDetail/normalizeUniversityUrl';
import './cityDetail/CityCompaniesMobile.css';
import MobileDiscoveryViewSwitch from './MobileDiscoveryViewSwitch';
import MobileDiscoverySearchToolbar from './MobileDiscoverySearchToolbar';
import MobileAppliedChips from './MobileAppliedChips';
import MobileCompanyResults from './MobileCompanyResults';
import MobileCompaniesSortDrawer from './MobileCompaniesSortDrawer';
import useMobileLoadMore from './useMobileLoadMore';
import { captureDiscoveryFilters, buildDiscoveryFilterChipLabels } from './discoveryMobileFilters';
import { mobileCompanySortDefinitions, mobileCompanySortOptions, getMobileSortedCompanies } from './mobileCompanySorting';
import icSort from '../../assets/img/ic-sort.svg';
import NavTabs from '../../NavTabs';
import icFilter from '../../assets/img/ic-filter.svg';
import icStar from '../../assets/img/ic-star.svg';
import icLink from '../../assets/img/ic-link-square.svg';
import icHeartEmpty from '../../assets/img/ic-heart-empty.svg';
import icHeartFull from '../../assets/img/ic-heart-full.svg';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';

const PAGE_DISCOVERY_TYPE = 'companies';

// CONTRACT: this mapping must stay identical to AI_INDUSTRY_GROUPS in the backend's
// discoveryAiContextService.js (and in DiscoveryCompanyList.js/DiscoveryJobsList.js).
// Update all together.
const AI_INDUSTRY_GROUPS = {
    'financial services': ['64', '65', '66'],
    'software': ['62', '63'],
};

const getCompanySizeBucket = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw || raw === 'n/a' || raw === 'na' || raw === '-') return '';
    const compact = raw.replace(/employees?/g, '').replace(/\s+/g, ' ').trim();
    if (compact.includes('several 1000')) return '1,001 - 5,000 employees';
    if (compact.includes('under 30')) return '11 - 50 employees';
    if (compact.includes('over 100000')) return 'over 100000';
    const rangeMatch = compact.match(/(\d[\d,.\s]*)\s*[-–—]\s*(\d[\d,.\s]*)/);
    if (rangeMatch) {
        const min = Number(rangeMatch[1].replace(/[^\d]/g, ''));
        const max = Number(rangeMatch[2].replace(/[^\d]/g, ''));
        if (!Number.isNaN(min) && !Number.isNaN(max)) {
            if (max <= 10) return '1 - 10 employees';
            if (min >= 11 && max <= 50) return '11 - 50 employees';
            if (min >= 51 && max <= 200) return '51 - 200 employees';
            if (min >= 201 && max <= 500) return '201 - 500 employees';
            if (min >= 501 && max <= 1000) return '501 - 1,000 employees';
            if (min >= 1001 && max <= 5000) return '1,001 - 5,000 employees';
            if (min >= 5001 && max <= 10000) return '5,001 - 10,000 employees';
            if (min >= 10001 && max <= 50000) return '10001 - 50000 employees';
            if (min >= 50001 && max <= 100000) return '50001 - 100000';
            if (min > 100000) return 'over 100000';
            const avg = (min + max) / 2;
            if (avg <= 10) return '1 - 10 employees';
            if (avg <= 50) return '11 - 50 employees';
            if (avg <= 200) return '51 - 200 employees';
            if (avg <= 500) return '201 - 500 employees';
            if (avg <= 1000) return '501 - 1,000 employees';
            if (avg <= 5000) return '1,001 - 5,000 employees';
            if (avg <= 10000) return '5,001 - 10,000 employees';
            if (avg <= 50000) return '10001 - 50000 employees';
            if (avg <= 100000) return '50001 - 100000';
            return 'over 100000';
        }
    }
    const numericValue = Number(compact.replace(/[^\d]/g, ''));
    if (!Number.isNaN(numericValue) && numericValue > 0) {
        if (numericValue <= 10) return '1 - 10 employees';
        if (numericValue <= 50) return '11 - 50 employees';
        if (numericValue <= 200) return '51 - 200 employees';
        if (numericValue <= 500) return '201 - 500 employees';
        if (numericValue <= 1000) return '501 - 1,000 employees';
        if (numericValue <= 5000) return '1,001 - 5,000 employees';
        if (numericValue <= 10000) return '5,001 - 10,000 employees';
        if (numericValue <= 50000) return '10001 - 50000 employees';
        if (numericValue <= 100000) return '50001 - 100000';
        return 'over 100000';
    }
    return '';
};

const DiscoveryCityCompanyList = () => {
    const navigate = useNavigate();
    const { id, cityId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const actualCityId = cityId || id;
    const countryCode = cityId ? id : null;

    const [cityUrl, setCityUrl] = useState('');
    const [cityName, setCityName] = useState('');
    const [countryName, setCountryName] = useState('');
    // Keep the last explicitly applied comparator when fresh API data arrives.
    const refreshSortRef = useRef(null);
    const [companies, setCompanies] = useState([]);
    const [industriesData, setIndustriesData] = useState([]);
    const [favorites, setFavorites] = useState(new Set());

    const [sortOrderName, setSortOrderName] = useState('asc');
    const [sortOrderIndustry, setSortOrderIndustry] = useState('asc');
    const [sortOrderGlobalSize, setSortOrderGlobalSize] = useState('asc');
    const [sortOrderRating, setSortOrderRating] = useState('asc');
    const [sortOrderLastUpdate, setSortOrderLastUpdate] = useState('asc');

    const [currentPage, setCurrentPage] = useState(1);
    const [companiesPerPage, setCompaniesPerPage] = useState(10);

    // Initialized once from the URL; ?industry=Financial Services stays a group match
    // for the life of the page rather than a single selectedIndustry code.
    const [aiIndustryGroup, setAiIndustryGroup] = useState(() => {
        const rawParam = String(searchParams.get('industry') || '').trim();
        return AI_INDUSTRY_GROUPS[rawParam.toLowerCase()] ? rawParam : null;
    });

    const clearAiIndustryGroup = () => {
        setAiIndustryGroup(null);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('industry');
            return next;
        }, { replace: true });
    };

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [selectedStudyDirection, setSelectedStudyDirection] = useState('');
    const [selectedCompanySize, setSelectedCompanySize] = useState('');
    const [selectedCompanyRating, setSelectedCompanyRating] = useState('');
    const [selectedEnglishSpeaking, setSelectedEnglishSpeaking] = useState(
        searchParams.get('englishSpeaking') === 'true' ? true : null
    );
    const [selectedWorldTraveler, setSelectedWorldTraveler] = useState(null);
    const [selectedStudentJobAreas, setSelectedStudentJobAreas] = useState(
        searchParams.get('studentFriendly') === 'true' ? true : null
    );
    const [selectedFavorites, setSelectedFavorites] = useState(null);
    const [savedFilterSets, setSavedFilterSets] = useState([]);
    const [selectedFilterSet, setSelectedFilterSet] = useState('default');
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    const [showMobileSort, setShowMobileSort] = useState(false);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const [draftSortField, setDraftSortField] = useState(null);
    const [draftSortDirection, setDraftSortDirection] = useState(null);
    const [appliedMobileFilters, setAppliedMobileFilters] = useState(() => captureDiscoveryFilters({}));

    // Matches the City detail shell breakpoint used across Discovery pages.
    const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });

    const industryNameByCode = useMemo(() => {
        const map = new Map();
        (industriesData || []).forEach((industry) => {
            if (industry?.code) {
                map.set(String(industry.code), industry.name || String(industry.code));
            }
        });
        return map;
    }, [industriesData]);

    const currentFilterValues = captureDiscoveryFilters({ selectedCity, selectedIndustry, aiIndustryGroup, selectedStudyDirection, selectedCompanySize, selectedCompanyRating, selectedEnglishSpeaking, selectedWorldTraveler, selectedStudentJobAreas, selectedFavorites });
    const effectiveFilters = isMobile && showMobileFilters ? appliedMobileFilters : currentFilterValues;
    const filteredCompanies = companies.filter((company) => {
        const { selectedCity, selectedIndustry, aiIndustryGroup, selectedStudyDirection, selectedCompanySize, selectedCompanyRating, selectedEnglishSpeaking, selectedWorldTraveler, selectedStudentJobAreas, selectedFavorites } = effectiveFilters;
        const normalizeStudyDirection = (val) =>
            String(val).trim()
                .replace(/^\d+(?:\.\d+)?\s*[-–—]?\s*/, '')
                .replace(/([.:])\s*\d+(?:\.\d+)?\s*[-–—]?\s*/g, '$1 ')
                .replace(/^\(\s*/, '')
                .replace(/\)\s*$/, '')
                .replace(/\s{2,}/g, ' ')
                .trim()
                .toLowerCase();

        const matchesSearch =
            !searchQuery ||
            company.title?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesCity =
            !selectedCity ||
            String(company.cityRegion || '').trim() === String(selectedCity || '').trim();

        const aiIndustryGroupCodes = aiIndustryGroup
            ? AI_INDUSTRY_GROUPS[aiIndustryGroup.toLowerCase()]
            : null;

        const matchesIndustry = aiIndustryGroupCodes
            ? (Array.isArray(company.industries) &&
                company.industries.some((entry) =>
                    aiIndustryGroupCodes.includes(String(entry?.industry || '').trim())
                ))
            : (
                !selectedIndustry ||
                (Array.isArray(company.industries) &&
                    company.industries.some((entry) => entry?.industry === selectedIndustry))
            );

        const matchesStudyDirection =
            !selectedStudyDirection ||
            (() => {
                const rawStudyDirections = company.studyFields?.preferredStudyDirections;
                if (Array.isArray(rawStudyDirections)) {
                    return rawStudyDirections.some(
                        (direction) => normalizeStudyDirection(direction) === normalizeStudyDirection(selectedStudyDirection)
                    );
                }
                if (typeof rawStudyDirections === 'string') {
                    return rawStudyDirections
                        .split(',')
                        .map((direction) => normalizeStudyDirection(direction))
                        .includes(normalizeStudyDirection(selectedStudyDirection));
                }
                return false;
            })();

        const companySizeBucket = getCompanySizeBucket(company.globalSize || company.localSize || '');
        const matchesCompanySize = !selectedCompanySize || companySizeBucket === selectedCompanySize;

        const companyRatingValue = Number(company.googleRating);
        const matchesCompanyRating =
            !selectedCompanyRating ||
            (!Number.isNaN(companyRatingValue) && companyRatingValue >= Number(selectedCompanyRating));

        const matchesEnglishSpeaking =
            selectedEnglishSpeaking === null || company.openedEnglishSpeaking === true;

        const matchesWorldTraveler =
            selectedWorldTraveler === null || company.openedWorldTraveler === true;

        const matchesStudentJobAreas =
            selectedStudentJobAreas === null ||
            (Array.isArray(company.studentJobAreas)
                ? company.studentJobAreas.length > 0
                : Boolean(company.studentJobAreas));

        const matchesFavorites =
            selectedFavorites === null || favorites.has(String(company._id));

        return (
            matchesSearch &&
            matchesCity &&
            matchesIndustry &&
            matchesStudyDirection &&
            matchesCompanySize &&
            matchesCompanyRating &&
            matchesEnglishSpeaking &&
            matchesWorldTraveler &&
            matchesStudentJobAreas &&
            matchesFavorites
        );
    });

    const indexOfLastCompany = currentPage * companiesPerPage;
    const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
    const currentCompanies = filteredCompanies.slice(indexOfFirstCompany, indexOfLastCompany);
    const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);
    const noCompanies = filteredCompanies.length === 0;

    const mobilePagination = useMobileLoadMore({
        items: getMobileSortedCompanies(filteredCompanies, sortField, sortDirection),
        filterKey: JSON.stringify({ actualCityId, countryCode, searchQuery, ...effectiveFilters, sortField, sortDirection }),
        storageKey: `discovery-city-${countryCode || 'global'}-${actualCityId}-companies-mobile-list`,
        isMobile,
    });


    useEffect(() => {
        setCurrentPage(1);
    }, [
        searchQuery,
        selectedCity,
        selectedIndustry,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedFavorites,
    ]);

    const buildCurrentFiltersPayload = () => ({
        searchQuery,
        selectedCity,
        selectedIndustry,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedFavorites,
    });

    const applySavedFilterSet = (filters = {}) => {
        setSearchQuery(filters.searchQuery || '');
        setSelectedCity(isMobile ? '' : filters.selectedCity || '');
        setSelectedIndustry(filters.selectedIndustry || '');
        setSelectedStudyDirection(filters.selectedStudyDirection || '');
        setSelectedCompanySize(filters.selectedCompanySize || '');
        setSelectedCompanyRating(filters.selectedCompanyRating || '');
        setSelectedEnglishSpeaking(filters.selectedEnglishSpeaking === true ? true : null);
        setSelectedWorldTraveler(filters.selectedWorldTraveler === true ? true : null);
        setSelectedStudentJobAreas(filters.selectedStudentJobAreas === true ? true : null);
        setSelectedFavorites(filters.selectedFavorites === true ? true : null);
    };

    const handleSaveFilters = async (filterSetName) => {
        try {
            const trimmedName = String(filterSetName || '').trim();
            if (!trimmedName) return;
            const payload = {
                page: 'city-companies',
                name: trimmedName,
                filters: buildCurrentFiltersPayload(),
            };
            const res = await api.post('/api/discovery-filter-sets', payload);
            const savedFilterSet = res.data;
            setSavedFilterSets((prev) => {
                const withoutDuplicate = prev.filter(
                    (item) => item._id !== savedFilterSet._id && item.name !== savedFilterSet.name
                );
                return [savedFilterSet, ...withoutDuplicate];
            });
            setSelectedFilterSet(savedFilterSet._id);
        } catch (error) {
            console.error('Error saving discovery city company filter set:', error);
        }
    };

    const handleDeleteFilterSet = async (filterSetId) => {
        if (!filterSetId || filterSetId === 'default') return;
        try {
            await api.delete(`/api/discovery-filter-sets/${filterSetId}`);
            const wasSelected = String(selectedFilterSet) === String(filterSetId);
            setSavedFilterSets((prev) =>
                prev.filter((filterSet) => String(filterSet._id) !== String(filterSetId))
            );
            if (wasSelected) {
                setSelectedFilterSet('default');
                applySavedFilterSet({});
            }
        } catch (err) {
            console.error('Error deleting filter set:', err);
        }
    };

    const handleFilterSetChange = (value) => {
        setSelectedFilterSet(value);
        if (value === 'default') {
            applySavedFilterSet({});
            return;
        }
        const selectedSavedFilterSet = savedFilterSets.find((item) => item._id === value);
        if (selectedSavedFilterSet?.filters) {
            applySavedFilterSet(selectedSavedFilterSet.filters);
        }
    };

    useDiscoveryFetch(({ signal, isCurrent }) => {
        const fetchData = async () => {
            try {
                const [cityRes, companiesRes, industriesRes, filterSetsRes, currentUserRes] = await Promise.all([
                    api.get(`/api/discovery/cities/${actualCityId}`, { signal }),
                    api.get(`/api/discovery/cities/${actualCityId}/companies`, { signal }),
                    api.get('/api/nace', { signal }),
                    api.get('/api/discovery-filter-sets?page=city-companies', { signal }),
                    api.get('/api/users/me', { signal }),
                ]);
                if (!isCurrent()) return;

                const cityData = cityRes.data;
                setCityName(cityData?.cityName || cityData?.name || id);
                setCityUrl(cityData?.url || '');
                setCountryName(cityData?.country?.name || '');

                const safeCompanies = Array.isArray(companiesRes.data) ? companiesRes.data : [];
                const safeIndustries = Array.isArray(industriesRes.data) ? industriesRes.data : [];
                const safeFilterSets = Array.isArray(filterSetsRes.data) ? filterSetsRes.data : [];
                const safeFavoriteCompanies = Array.isArray(currentUserRes?.data?.favoriteCompanies)
                    ? currentUserRes.data.favoriteCompanies.map((companyId) => String(companyId))
                    : [];

                setCompanies(refreshSortRef.current ? [...safeCompanies].sort(refreshSortRef.current) : safeCompanies);
                setIndustriesData(safeIndustries);
                setSavedFilterSets(safeFilterSets);
                setFavorites(new Set(safeFavoriteCompanies));
            } catch (error) {
                if (!isCurrent()) return;
                console.error('Error fetching city companies / industries / filter sets / favorites:', error);
            }
        };

        fetchData();
    }, [actualCityId]);

    // Resolves an industry display label (from a URL ?industry= param or directly from
    // an AI chip's structuredFilters.industry) to what selectedIndustry/aiIndustryGroup
    // actually filter on: a known AI_INDUSTRY_GROUPS key stays a group match; anything
    // else is fuzzy-matched against the NACE list to a single code. No match -> no-op.
    const resolveIndustryLabel = (label) => {
        const trimmedLabel = String(label || '').trim();
        if (!trimmedLabel) return;

        const normalizedLabel = trimmedLabel.toLowerCase();

        if (AI_INDUSTRY_GROUPS[normalizedLabel]) {
            setAiIndustryGroup(trimmedLabel);
            return;
        }

        if (industriesData.length === 0) return;

        const matchedIndustry = industriesData.find((industry) => {
            const normalizedName = String(industry.name || '').trim().toLowerCase();
            return (
                normalizedName === normalizedLabel ||
                normalizedName.includes(normalizedLabel) ||
                normalizedLabel.includes(normalizedName)
            );
        });

        if (matchedIndustry) {
            setSelectedIndustry(String(matchedIndustry.code));
        }
    };

    // One-shot: resolve an ?industry= query param the same way. Only runs once
    // industriesData has loaded, and only ever applies once.
    const appliedIndustryParamRef = useRef(false);

    useEffect(() => {
        if (appliedIndustryParamRef.current) return;
        if (industriesData.length === 0) return;

        appliedIndustryParamRef.current = true;

        const industryParam = searchParams.get('industry');
        if (!industryParam) return;

        // Broad AI industry groups are already applied via the aiIndustryGroup
        // initializer above; only the single-code fuzzy match needs to run here.
        if (AI_INDUSTRY_GROUPS[industryParam.trim().toLowerCase()]) return;

        resolveIndustryLabel(industryParam);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [industriesData, searchParams]);

    // This page's companies have no job-count data (no openJobsIntern), so an
    // internship-intent chip routes to this same city's Jobs page instead of
    // pretending to filter on it here. Everything else applies locally.
    const handleChipClick = (sf) => {
        if (!sf) return;

        if (sf.jobType === 'internship') {
            const params = new URLSearchParams();
            if (sf.industry) params.set('industry', sf.industry);
            if (sf.jobType === 'english-speaking' || sf.language === 'English') {
                params.set('englishSpeaking', 'true');
            }
            params.set('internship', 'true');

            navigate(`${basePath}/jobs?${params.toString()}`);
            return;
        }

        if (sf.city) setSelectedCity(sf.city);
        if (sf.industry) resolveIndustryLabel(sf.industry);

        if (sf.jobType === 'english-speaking' || sf.language === 'English') {
            setSelectedEnglishSpeaking(true);
        }

        if (sf.jobType === 'student') setSelectedStudentJobAreas(true);
    };

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext(
        {
            pageType: 'discovery-city-companies',
            discoveryType: PAGE_DISCOVERY_TYPE,
            cityId: actualCityId,
            cityName,
            countryName,
            activeTab: 'Companies',
            suggestions: getDiscoveryAiSuggestions({
                discoveryType: PAGE_DISCOVERY_TYPE,
                selectedCity: cityName,
                selectedCountry: countryName,
            }),
        },
        { onApplyStructuredFilters: handleChipClick }
    );

    const sortCompaniesByName = () => {
        const newSortOrder = sortOrderName === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const nameA = String(a.title || '').toLowerCase();
            const nameB = String(b.title || '').toLowerCase();
            return newSortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderName(newSortOrder);
    };

    const sortCompaniesByIndustry = () => {
        const newSortOrder = sortOrderIndustry === 'asc' ? 'desc' : 'asc';
        const getPrimaryIndustryLabel = (company) => {
            const firstIndustryCode = company?.industries?.[0]?.industry;
            if (!firstIndustryCode) return '';
            return industryNameByCode.get(String(firstIndustryCode)) || String(firstIndustryCode);
        };
        refreshSortRef.current = (a, b) => {
            const industryA = getPrimaryIndustryLabel(a).toLowerCase();
            const industryB = getPrimaryIndustryLabel(b).toLowerCase();
            return newSortOrder === 'asc' ? industryA.localeCompare(industryB) : industryB.localeCompare(industryA);
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderIndustry(newSortOrder);
    };

    const sortCompaniesByGlobalSize = () => {
        const newSortOrder = sortOrderGlobalSize === 'asc' ? 'desc' : 'asc';
        const getSize = (value) => {
            const raw = String(value || '').trim().toLowerCase();
            if (!raw || raw === 'n/a' || raw === 'na' || raw === '-') return 0;
            if (raw.includes('several 1000')) return 3000;
            if (raw.includes('under 30')) return 30;
            if (raw.includes('over 100000')) return 100001;
            const rangeMatch = raw.match(/(\d[\d,.\s]*)\s*[-–—]\s*(\d[\d,.\s]*)/);
            if (rangeMatch) {
                const min = Number(rangeMatch[1].replace(/[^\d]/g, ''));
                const max = Number(rangeMatch[2].replace(/[^\d]/g, ''));
                if (!Number.isNaN(min) && !Number.isNaN(max)) return (min + max) / 2;
            }
            const numericValue = Number(raw.replace(/[^\d]/g, ''));
            return Number.isNaN(numericValue) ? 0 : numericValue;
        };
        refreshSortRef.current = (a, b) => {
            const sizeA = getSize(a.globalSize || a.localSize);
            const sizeB = getSize(b.globalSize || b.localSize);
            return newSortOrder === 'asc' ? sizeA - sizeB : sizeB - sizeA;
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderGlobalSize(newSortOrder);
    };

    const sortCompaniesByRating = () => {
        const newSortOrder = sortOrderRating === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const ratingA = Number(a.googleRating) || 0;
            const ratingB = Number(b.googleRating) || 0;
            return newSortOrder === 'asc' ? ratingA - ratingB : ratingB - ratingA;
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderRating(newSortOrder);
    };

    const sortCompaniesByLastUpdate = () => {
        const newSortOrder = sortOrderLastUpdate === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const dateA = new Date(a.latestDataUpdate || a.updatedAt || 0).getTime();
            const dateB = new Date(b.latestDataUpdate || b.updatedAt || 0).getTime();
            return newSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderLastUpdate(newSortOrder);
    };

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const getPaginationItems = () => {
        const siblingCount = 2;
        const boundaryCount = 1;
        if (totalPages <= 1) return [1];
        const range = (start, end) => {
            const out = [];
            for (let i = start; i <= end; i++) out.push(i);
            return out;
        };
        const startPages = range(1, Math.min(boundaryCount, totalPages));
        const endPages = range(Math.max(totalPages - boundaryCount + 1, boundaryCount + 1), totalPages);
        const siblingsStart = Math.max(
            Math.min(currentPage - siblingCount, totalPages - boundaryCount - siblingCount * 2 - 1),
            boundaryCount + 2
        );
        const siblingsEnd = Math.min(
            Math.max(currentPage + siblingCount, boundaryCount + siblingCount * 2 + 2),
            totalPages - boundaryCount - 1
        );
        const items = [
            ...startPages,
            ...(siblingsStart > boundaryCount + 2 ? ['ellipsis'] : boundaryCount + 1 < totalPages - boundaryCount ? [boundaryCount + 1] : []),
            ...range(siblingsStart, siblingsEnd),
            ...(siblingsEnd < totalPages - boundaryCount - 1 ? ['ellipsis'] : totalPages - boundaryCount > boundaryCount ? [totalPages - boundaryCount] : []),
            ...endPages,
        ];
        return items.filter((v, i) => items.indexOf(v) === i);
    };

    const toggleFavorite = async (companyId) => {
        const companyIdStr = String(companyId);
        const previousFavorites = new Set(favorites);
        const wasFavorited = previousFavorites.has(companyIdStr);
        const optimisticFavorites = new Set(previousFavorites);
        if (wasFavorited) {
            optimisticFavorites.delete(companyIdStr);
        } else {
            optimisticFavorites.add(companyIdStr);
        }
        setFavorites(optimisticFavorites);
        try {
            const res = await api.patch(`/api/users/me/favorite-companies/${companyIdStr}`);
            const safeFavoriteCompanies = Array.isArray(res?.data?.favoriteCompanies)
                ? res.data.favoriteCompanies.map((favId) => String(favId))
                : [];
            setFavorites(new Set(safeFavoriteCompanies));

            const company = companies.find((c) => String(c._id) === companyIdStr);
            trackEvent(
                wasFavorited ? ANALYTICS_EVENTS.FAVORITE_COMPANY_REMOVED : ANALYTICS_EVENTS.FAVORITE_COMPANY_ADDED,
                {
                    companyId: companyIdStr,
                    companyName: company?.title || '',
                    level: 3,
                    source: 'city_companies_list',
                }
            );
        } catch (error) {
            console.error('Error toggling favorite company:', error);
            setFavorites(previousFavorites);
        }
    };

    const companiesWithCoordinates = filteredCompanies.filter((company) => {
        const lat = Number(company.latitude);
        const lng = Number(company.longitude);
        return !Number.isNaN(lat) && !Number.isNaN(lng);
    });

    const anyCompaniesWithCoordinates = companies.some((company) => {
        const lat = Number(company.latitude);
        const lng = Number(company.longitude);
        return !Number.isNaN(lat) && !Number.isNaN(lng);
    });

    const captureCurrentMobileFilters = () => captureDiscoveryFilters({
        selectedCity,
        selectedIndustry,
        aiIndustryGroup,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedFavorites,
    });
    const restoreMobileFilters = (filters) => {
        setSelectedCity(filters.selectedCity);
        setSelectedIndustry(filters.selectedIndustry);
        setAiIndustryGroup(filters.aiIndustryGroup);
        setSelectedStudyDirection(filters.selectedStudyDirection);
        setSelectedCompanySize(filters.selectedCompanySize);
        setSelectedCompanyRating(filters.selectedCompanyRating);
        setSelectedEnglishSpeaking(filters.selectedEnglishSpeaking);
        setSelectedWorldTraveler(filters.selectedWorldTraveler);
        setSelectedStudentJobAreas(filters.selectedStudentJobAreas);
        setSelectedFavorites(filters.selectedFavorites);
    };
    const closeMobileFilters = () => {
        restoreMobileFilters(appliedMobileFilters);
        setShowMobileFilters(false);
    };
    const openMobileFilters = () => {
        setAppliedMobileFilters(captureCurrentMobileFilters());
        setShowMobileFilters(true);
    };
    const applyMobileFilters = () => {
        if (appliedMobileFilters.aiIndustryGroup && !aiIndustryGroup) clearAiIndustryGroup();
        setAppliedMobileFilters(captureCurrentMobileFilters());
        setShowMobileFilters(false);
    };
    const clearMobileFiltersPreserveSearch = () => {
        const cleared = captureDiscoveryFilters({});
        restoreMobileFilters(cleared);
        if (!showMobileFilters) {
            setAppliedMobileFilters(cleared);
            clearAiIndustryGroup();
        }
    };
    const clearEmptyStateSearchAndFilters = () => {
        clearMobileFiltersPreserveSearch();
        setSearchQuery('');
    };
    const removeAppliedMobileFilter = (key) => {
        const booleanKeys = [
            'selectedEnglishSpeaking',
            'selectedWorldTraveler',
            'selectedStudentJobAreas',
            'selectedFavorites',
        ];
        const next = {
            ...currentFilterValues,
            [key]: booleanKeys.includes(key) || key === 'aiIndustryGroup' ? null : '',
        };
        restoreMobileFilters(next);
        setAppliedMobileFilters(next);
    };
    const mobileSortOptions = mobileCompanySortOptions.filter(({ field }) => field !== 'city' && field !== 'country');
    const activeMobileSortOption = mobileSortOptions.find((option) => option.field === sortField);
    const openMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(true);
    };
    const closeMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(false);
    };
    const clearMobileSort = () => {
        setSortField(null);
        setSortDirection(null);
    };
    const clearMobileSortFromDrawer = () => {
        clearMobileSort();
        setDraftSortField(null);
        setDraftSortDirection(null);
        setShowMobileSort(false);
    };
    const selectDraftSort = (field) => {
        setDraftSortDirection((direction) => (
            draftSortField === field
                ? (direction === 'asc' ? 'desc' : 'asc')
                : mobileCompanySortDefinitions[field].defaultDirection
        ));
        setDraftSortField(field);
    };
    const applyMobileSort = () => {
        setSortField(draftSortField);
        setSortDirection(draftSortDirection);
        setShowMobileSort(false);
    };
    const appliedMobileChips = buildDiscoveryFilterChipLabels(
        !showMobileFilters ? currentFilterValues : appliedMobileFilters,
        industryNameByCode
    )
        .filter((chip) => chip.key !== 'selectedCountry')
        .map((chip) => ({ ...chip, onRemove: () => removeAppliedMobileFilter(chip.key) }));
    if (activeMobileSortOption) {
        appliedMobileChips.push({
            key: 'activeSort',
            label: activeMobileSortOption.label,
            icon: sortDirection === 'asc' ? <FaCaretUp aria-hidden="true" /> : <FaCaretDown aria-hidden="true" />,
            onOpen: openMobileSort,
            onRemove: clearMobileSort,
            removeLabel: `Clear ${activeMobileSortOption.label} sort`,
        });
    }

    const basePath = countryCode
        ? `/discovery/countries/${countryCode}/cities/${actualCityId}`
        : `/discovery/cities/${actualCityId}`;
    const cityNavItems = [
        { to: basePath, eventKey: 'about', label: 'General Info', end: true },
        { to: `${basePath}/jobs`, eventKey: 'companies', label: 'Companies & Jobs', alsoActive: [`${basePath}/companies`] },
        { to: `${basePath}/salary`, eventKey: 'salary', label: 'Average salaries' },
        { to: `${basePath}/insights`, eventKey: 'insights', label: 'City insights' },
        { to: `${basePath}/universities`, eventKey: 'universities', label: 'Universities' },
        { to: `${basePath}/comments`, eventKey: 'comments', label: 'Comments' },
    ];

    return (
        <>
            <Sidebar />
            <main className={`main-content flex-grow-1${isMobile ? '' : ' discovery-map-page'}`}>
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb cityName={cityName} subPage="Companies" fromCountry={countryCode} countryName={countryName || undefined} />
                        </Col>
                    </Row>

                    {isMobile ? (
                        <Row className="g-0 country-detail-mobile city-detail-mobile">
                            <Col className="p-0">
                                <CityDetailHero cityName={cityName} countryName={countryName}
                                    url={normalizeUrl(cityUrl) ? { href: normalizeUrl(cityUrl), label: cityUrl } : null} />
                                <CityDetailMobileTabs items={cityNavItems} />
                                <section className="city-discovery-list-content city-companies-section">
                                    <MobileDiscoveryViewSwitch activeView="companies"
                                        routes={{ companies: `${basePath}/companies`, jobs: `${basePath}/jobs` }} />
                                    <MobileDiscoveryHeading title="Companies" />
                                    <MobileDiscoverySearchToolbar
                                        searchValue={searchQuery} onSearchChange={setSearchQuery}
                                        searchPlaceholder="Search..." searchAriaLabel="Search companies"
                                        actions={[
                                            { label: 'Open filters', icon: icFilter, onClick: openMobileFilters },
                                            { label: 'Sort companies', icon: icSort, onClick: openMobileSort },
                                        ]} />
                                    <MobileAppliedChips chips={appliedMobileChips} />
                                    <DiscoveryFilterBar
                                        title="Companies"
                                        items={companies}
                                        industryNameByCode={industryNameByCode}
                                        showCountryFilter={false}
                                        showCityFilter={false}
                                        cityField="cityRegion"
                                        searchQuery={searchQuery}
                                        setSearchQuery={setSearchQuery}
                                        selectedCity={selectedCity}
                                        setSelectedCity={setSelectedCity}
                                        selectedIndustry={selectedIndustry}
                                        setSelectedIndustry={setSelectedIndustry}
                                        selectedStudyDirection={selectedStudyDirection}
                                        setSelectedStudyDirection={setSelectedStudyDirection}
                                        selectedCompanySize={selectedCompanySize}
                                        setSelectedCompanySize={setSelectedCompanySize}
                                        selectedCompanyRating={selectedCompanyRating}
                                        setSelectedCompanyRating={setSelectedCompanyRating}
                                        selectedEnglishSpeaking={selectedEnglishSpeaking}
                                        setSelectedEnglishSpeaking={setSelectedEnglishSpeaking}
                                        selectedWorldTraveler={selectedWorldTraveler}
                                        setSelectedWorldTraveler={setSelectedWorldTraveler}
                                        selectedStudentJobAreas={selectedStudentJobAreas}
                                        setSelectedStudentJobAreas={setSelectedStudentJobAreas}
                                        aiIndustryGroupLabel={aiIndustryGroup}
                                        onClearAiIndustryGroup={() => setAiIndustryGroup(null)}
                                        selectedFavorites={selectedFavorites}
                                        setSelectedFavorites={setSelectedFavorites}
                                        savedFilterSets={savedFilterSets}
                                        selectedFilterSet={selectedFilterSet}
                                        onFilterSetChange={handleFilterSetChange}
                                        onDeleteFilterSet={handleDeleteFilterSet}
                                        onSaveFilters={handleSaveFilters}
                                        mobileModalOnly
                                        mobileModalOpen={showMobileFilters}
                                        onMobileModalClose={closeMobileFilters}
                                        onMobileModalApply={applyMobileFilters}
                                        onClearFilters={clearMobileFiltersPreserveSearch}
                                        hideSearchInMobileModal
                                        mobileModalTitle="Filter companies"
                                        mobileApplyLabel="Apply filters"
                                        hideTypeToggleOnMobile
                                        hideActiveFilterSummaryOnMobile
                                        useFullScreenMobilePanel
                                        mobileSavedFilterLabel="Saved filter"
                                        mobileSaveFilterLabel="Save current filters"
                                        compactSavedFilterOnMobile
                                    />
                                    <MobileCompanyResults mobilePagination={mobilePagination}
                                        favorites={favorites} onFavoriteToggle={toggleFavorite} hideCity
                                        onClearFilters={clearEmptyStateSearchAndFilters} onOpenFilters={openMobileFilters}
                                        onOpenCompany={(company) => {
                                            mobilePagination.rememberScrollPosition();
                                            trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                companyId: company._id, companyName: company.title,
                                                source: 'city_companies_list', level: 3, viewMode: 'companies',
                                            });
                                            navigate(`${basePath}/companies/${company._id}`);
                                        }} />
                                </section>
                            </Col>
                        </Row>
                    ) : (
                    <Row className="mb-4">
                        <Col>
                            <Card className="discovery-shell-card">
                                <Card.Body>
                                    <Row className="discovery-page-title-row">
                                        <Col xs={12}>
                                            <h1>{cityName ? `${cityName}` : 'Companies'}</h1>
                                        </Col>
                                    </Row>

                                    {anyCompaniesWithCoordinates && (
                                        <Row className="discovery-map-row">
                                            <Col xs={12} className="px-0">
                                                <div className="discovery-mobile-map-area">
                                                    <DiscoveryMap
                                                        items={companiesWithCoordinates}
                                                        height={260}
                                                        markerScale={0.6}
                                                        clusterPixelRadius={20}
                                                        maxClusterZoom={9}
                                                        groupClickZoom={14}
                                                        canCluster={(a, b) => a.country === b.country}
                                                        getLatitude={(company) => company.latitude}
                                                        getLongitude={(company) => company.longitude}
                                                        getTitle={(company) => company.title}
                                                        getLogo={(company) => company.logo || defaultCompanyMarkerLogo}
                                                        getLink={(company) => `${basePath}/companies/${company._id}`}
                                                        onLinkClick={(company) => trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                            companyId: company._id,
                                                            companyName: company.title,
                                                            source: 'city_companies_map',
                                                            level: 3,
                                                            viewMode: 'companies',
                                                        })}
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    <Row className="mb-4">
                                        <Col>
                                            <NavTabs items={cityNavItems} />
                                        </Col>
                                    </Row>

                                    {!isMobile && (
                                        <Row className="mb-3">
                                            <Col xs={12}>
                                                <DiscoveryFilterBar
                                                    title="Companies"
                                                    viewOptions={[
                                                        { value: 'companies', label: 'Companies' },
                                                        { value: 'jobs', label: 'Jobs' },
                                                    ]}
                                                    activeView="companies"
                                                    onViewChange={(view) => {
                                                        if (view === 'jobs') navigate(`${basePath}/jobs`);
                                                    }}
                                                    items={companies}
                                                    industryNameByCode={industryNameByCode}
                                                    showCountryFilter={false}
                                                    showCityFilter={false}
                                                    cityField="cityRegion"
                                                    searchQuery={searchQuery}
                                                    setSearchQuery={setSearchQuery}
                                                    selectedCity={selectedCity}
                                                    setSelectedCity={setSelectedCity}
                                                    selectedIndustry={selectedIndustry}
                                                    setSelectedIndustry={setSelectedIndustry}
                                                    selectedStudyDirection={selectedStudyDirection}
                                                    setSelectedStudyDirection={setSelectedStudyDirection}
                                                    selectedCompanySize={selectedCompanySize}
                                                    setSelectedCompanySize={setSelectedCompanySize}
                                                    selectedCompanyRating={selectedCompanyRating}
                                                    setSelectedCompanyRating={setSelectedCompanyRating}
                                                    selectedEnglishSpeaking={selectedEnglishSpeaking}
                                                    setSelectedEnglishSpeaking={setSelectedEnglishSpeaking}
                                                    selectedWorldTraveler={selectedWorldTraveler}
                                                    setSelectedWorldTraveler={setSelectedWorldTraveler}
                                                    selectedStudentJobAreas={selectedStudentJobAreas}
                                                    setSelectedStudentJobAreas={setSelectedStudentJobAreas}
                                                    aiIndustryGroupLabel={aiIndustryGroup}
                                                    onClearAiIndustryGroup={clearAiIndustryGroup}
                                                    selectedFavorites={selectedFavorites}
                                                    setSelectedFavorites={setSelectedFavorites}
                                                    savedFilterSets={savedFilterSets}
                                                    selectedFilterSet={selectedFilterSet}
                                                    onFilterSetChange={handleFilterSetChange}
                                                    onDeleteFilterSet={handleDeleteFilterSet}
                                                    onSaveFilters={handleSaveFilters}
                                                />
                                            </Col>
                                        </Row>
                                    )}

                                    {noCompanies ? (
                                        <Row>
                                            <Col md={12}>
                                                <p className="mb-0">No companies available.</p>
                                            </Col>
                                        </Row>
                                    ) : (
                                        <Row>
                                            <Col>
                                                <div className="table-responsive list">
                                                    <Table className="discovery-table">
                                                        <thead>
                                                            <tr>
                                                                <th onClick={sortCompaniesByName} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Company
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortCompaniesByIndustry} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Industry
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortCompaniesByGlobalSize} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Global Size
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortCompaniesByRating} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Rating
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th>
                                                                    <div>Website</div>
                                                                </th>
                                                                <th onClick={sortCompaniesByLastUpdate} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Last Update
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th>Fav</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {currentCompanies.map((company) => (
                                                                <tr
                                                                    className="company-row"
                                                                    key={company._id}
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => {
                                                                        trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                                            companyId: company._id,
                                                                            companyName: company.title,
                                                                            source: 'city_companies_list',
                                                                            level: 3,
                                                                            viewMode: 'companies',
                                                                        });
                                                                        navigate(`${basePath}/companies/${company._id}`);
                                                                    }}
                                                                >
                                                                    <td>
                                                                        <div>
                                                                            <img
                                                                                src={company.logo ? company.logo : defaultLogo}
                                                                                alt={company.title}
                                                                                className="round"
                                                                            />
                                                                            <span className="text-ellipsis text-ellipsis-country-name">
                                                                                <strong>{company.title}</strong>
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        {Array.isArray(company.industries) && company.industries.length > 0
                                                                            ? (industryNameByCode.get(String(company.industries[0]?.industry)) ||
                                                                                company.industries[0]?.industry ||
                                                                                'n/a')
                                                                            : 'n/a'}
                                                                    </td>
                                                                    <td>{company.globalSize || 'n/a'}</td>
                                                                    <td>
                                                                        {company.googleRating > 0 ? (
                                                                            <span className="d-inline-flex align-items-center gap-1">
                                                                                {company.googleRating.toFixed(1)}
                                                                                <img src={icStar} alt="rating" style={{ width: '14px', height: '14px' }} />
                                                                            </span>
                                                                        ) : '—'}
                                                                    </td>
                                                                    <td>
                                                                        {company.url ? (
                                                                            <a
                                                                                href={company.url.startsWith('http') ? company.url : `https://${company.url}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="d-inline-flex align-items-center"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    trackEvent(ANALYTICS_EVENTS.COMPANY_WEBSITE_CLICKED, {
                                                                                        companyId: company._id,
                                                                                        companyName: company.title,
                                                                                        urlType: 'website',
                                                                                        level: 3,
                                                                                        source: 'city_companies_list',
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <img src={icLink} alt="website" style={{ width: '16px', height: '16px' }} />
                                                                            </a>
                                                                        ) : '—'}
                                                                    </td>
                                                                    <td>
                                                                        {company.latestDataUpdate || company.updatedAt
                                                                            ? new Date(company.latestDataUpdate || company.updatedAt).toLocaleDateString()
                                                                            : '—'}
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className="cursor-pointer d-inline-flex align-items-center"
                                                                            style={{ cursor: 'pointer' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleFavorite(company._id);
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={favorites.has(String(company._id)) ? icHeartFull : icHeartEmpty}
                                                                                alt="favorite"
                                                                                style={{ width: '16px', height: '16px' }}
                                                                            />
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    <Row className="pagination-wrapper">
                                        <Col className="d-flex align-items-center">
                                            <span>
                                                Total {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'}
                                            </span>
                                            <Pagination>
                                                <Pagination.Prev
                                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                    disabled={currentPage === 1}
                                                >
                                                    <BsChevronLeft />
                                                </Pagination.Prev>
                                                {getPaginationItems().map((item, index) =>
                                                    item === 'ellipsis' ? (
                                                        <Pagination.Ellipsis key={`e-${index}`} disabled />
                                                    ) : (
                                                        <Pagination.Item
                                                            key={item}
                                                            active={item === currentPage}
                                                            onClick={() => handlePageChange(item)}
                                                        >
                                                            {item}
                                                        </Pagination.Item>
                                                    )
                                                )}
                                                <Pagination.Next
                                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                    disabled={currentPage === totalPages || totalPages === 0}
                                                >
                                                    <BsChevronRight />
                                                </Pagination.Next>
                                            </Pagination>
                                        </Col>

                                        <Col className="pagination-controls">
                                            <div className="pagination-controls-per-page">
                                                <Form.Select
                                                    value={companiesPerPage}
                                                    onChange={(e) => {
                                                        setCompaniesPerPage(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    <option value="10">10 / page</option>
                                                    <option value="20">20 / page</option>
                                                    <option value="40">40 / page</option>
                                                </Form.Select>
                                            </div>

                                            <label className="pagination-controls-goto-label" htmlFor="goto-page-city-companies">
                                                Go to
                                            </label>

                                            <Form.Control
                                                id="goto-page-city-companies"
                                                className="pagination-controls-goto-input"
                                                type="number"
                                                value={currentPage}
                                                onChange={(e) => {
                                                    const newPage = Number(e.target.value);
                                                    if (newPage >= 1 && newPage <= totalPages) {
                                                        setCurrentPage(newPage);
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (currentPage < 1) setCurrentPage(1);
                                                    if (currentPage > totalPages) setCurrentPage(totalPages || 1);
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    )}
                </Container>
            </main>
            {isMobile && (
                <MobileCompaniesSortDrawer
                showMobileSort={showMobileSort}
                closeMobileSort={closeMobileSort}
                mobileSortOptions={mobileSortOptions}
                draftSortField={draftSortField}
                draftSortDirection={draftSortDirection}
                selectDraftSort={selectDraftSort}
                clearMobileSortFromDrawer={clearMobileSortFromDrawer}
                applyMobileSort={applyMobileSort}
            />
            )}
        </>
    );
};

export default DiscoveryCityCompanyList;
